import {
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { NavLink, useLocation } from "react-router";
import { BrandMark } from "./BrandMark";
import { siteConfig } from "../config/site";
import { projects } from "../data/projects";

interface AppShellProps {
  children: ReactNode;
}

const baseTabs = [
  { to: "/", label: "HOME", index: "00", end: true },
  { to: "/info", label: "INFO", index: "I", end: false },
  { to: "/games", label: "GAMES", index: "G", end: false },
  { to: "/contact", label: "CONTACT", index: "C", end: true },
];

function isGamesRoute(pathname: string) {
  return pathname === "/games" || pathname.startsWith("/games/");
}

function getRouteLabel(pathname: string) {
  if (pathname === "/") return "WELCOME";
  if (pathname === "/info") return "COMPANY INFORMATION";
  if (pathname === "/games") return "GAME INDEX";
  if (pathname === "/contact") return "CONTACT // MESSAGE FORM";

  const project = projects.find(
    (entry) => pathname === `/games/${entry.slug}`,
  );

  return project ? `GAME ${project.index} // ${project.title}` : "UNKNOWN ROUTE";
}

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  const gamesNavigationRef = useRef<HTMLElement>(null);
  const routeLabel = getRouteLabel(location.pathname);
  const showSubTabs = isGamesRoute(location.pathname);
  const tabProjects = projects.filter((project) => project.showInTabs);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: "auto" });
    gamesNavigationRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: "nearest", inline: "nearest" });
    document.title =
      location.pathname === "/"
        ? siteConfig.name
        : `${routeLabel} — ${siteConfig.name}`;
  }, [location.pathname, routeLabel]);

  return (
    <div className="app-background min-h-dvh overflow-hidden bg-[#050505] text-zinc-100">
      <div className="background-mark" aria-hidden="true" />

      <main className="relative grid min-h-dvh place-items-center p-0 sm:p-4 lg:p-8">
        <section
          id="app-frame"
          className="window-frame flex h-dvh w-full max-w-[1280px] flex-col overflow-hidden border border-zinc-700/80 bg-[#090909] sm:h-[min(860px,calc(100dvh-2rem))]"
          aria-label="Project Despair website"
        >
          <header className="flex h-11 shrink-0 items-center border-b border-zinc-700/80 bg-[#070707]">
            <NavLink
              to="/"
              aria-label="Project Despair home"
              className="group grid h-full w-12 shrink-0 place-items-center border-r border-zinc-700/80 transition-colors hover:bg-zinc-100 focus-visible:bg-zinc-100"
            >
              <BrandMark className="h-6 w-6 text-zinc-100 transition-colors group-hover:text-[#070707] group-focus-visible:text-[#070707]" />
            </NavLink>

            <div className="min-w-0 flex-1 px-3">
              <p className="truncate font-mono text-[9px] font-semibold tracking-[0.14em] text-zinc-300 sm:text-[11px] sm:tracking-[0.2em]">
                {siteConfig.terminalName}
              </p>
            </div>

            <div className="hidden h-full items-center border-l border-zinc-700/80 px-4 font-mono text-[9px] tracking-[0.18em] text-zinc-500 sm:flex">
              <span className="status-dot mr-2" aria-hidden="true" />
              ONLINE
            </div>

            <div
              className="flex h-full items-center gap-1.5 border-l border-zinc-700/80 px-3"
              aria-hidden="true"
            >
              <span className="window-control" />
              <span className="window-control window-control-filled" />
              <span className="window-control" />
            </div>
          </header>

          <nav
            className="tab-scroll flex h-12 shrink-0 overflow-x-auto border-b border-zinc-700/80 bg-[#0c0c0c]"
            aria-label="Primary navigation"
          >
            {baseTabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  [
                    "tab-link group flex shrink-0 items-center gap-2 border-r border-zinc-700/80 px-4 font-mono text-[10px] font-semibold tracking-[0.14em] transition-colors focus-visible:outline-none",
                    isActive
                      ? "bg-zinc-100 text-black"
                      : "text-zinc-500 hover:bg-zinc-900 hover:text-zinc-100 focus-visible:bg-zinc-900 focus-visible:text-zinc-100",
                  ].join(" ")
                }
              >
                <span className="text-[8px] opacity-55">{tab.index}</span>
                {tab.label}
              </NavLink>
            ))}

            <div className="min-w-10 flex-1" aria-hidden="true" />
          </nav>

          {showSubTabs && (
            <nav
              ref={gamesNavigationRef}
              className="tab-scroll flex h-[38px] shrink-0 items-stretch overflow-x-auto border-b border-zinc-700/80 bg-[#090909] pl-1"
              aria-label="Games navigation"
            >
              <NavLink
                to="/games"
                end
                className={({ isActive }) =>
                  ["subtab-link", isActive ? "subtab-link-active" : ""].join(" ")
                }
              >
                <span className="text-[8px] opacity-50">//</span>
                ALL GAMES
              </NavLink>

              {tabProjects.map((project) => (
                <NavLink
                  key={project.slug}
                  to={`/games/${project.slug}`}
                  className={({ isActive }) =>
                    ["subtab-link", isActive ? "subtab-link-active" : ""].join(" ")
                  }
                >
                  <span className="text-[8px] opacity-50">{project.index}</span>
                  {project.tabLabel}
                </NavLink>
              ))}

              <div className="min-w-6 flex-1" aria-hidden="true" />
            </nav>
          )}

          <div
            ref={contentRef}
            className="app-content relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
          >
            <div
              key={location.pathname}
              className="route-enter min-h-full"
            >
              {children}
            </div>
          </div>

          <footer className="grid h-8 shrink-0 grid-cols-[auto_1fr] items-center border-t border-zinc-700/80 bg-[#070707] font-mono text-[9px] tracking-[0.16em] text-zinc-600 sm:grid-cols-[auto_1fr_auto]">
            <span className="flex h-full items-center whitespace-nowrap border-r border-zinc-700/80 px-3">
              PD_SYS / {new Date().getFullYear()}
            </span>
            <span className="hidden truncate px-3 text-center sm:block">
              {routeLabel}
            </span>
            <span className="flex h-full items-center justify-self-end whitespace-nowrap border-l border-zinc-700/80 px-3">
              CLOUDFLARE EDGE
            </span>
          </footer>
        </section>
      </main>
    </div>
  );
}
